import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Send, Mail, Loader2 } from "lucide-react";
import { moduleApi } from "../../lib/api";
import { TokenPicker } from "../ui/TokenPicker";

const inputCls = "w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors";
const labelCls = "text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block";

const DEFAULT_SUBJECT = "Informations importantes pour votre réservation";
const DEFAULT_BODY = "Bonjour {{prenom}},\n\nMerci pour votre réservation pour {{evenement}}, à {{heure}} pour {{personnes}} personnes.\n\nNous tenons à vous informer que nous sommes sur un menu unique à 98€ par personne pour cette soirée.\n\nÀ bientôt,\n{{entreprise}}";

const TOKENS = [
  { token: "{{prenom}}", desc: "Prénom" },
  { token: "{{nom}}", desc: "Nom" },
  { token: "{{personnes}}", desc: "Total couverts" },
  { token: "{{adultes}}", desc: "Adultes" },
  { token: "{{enfants}}", desc: "Enfants" },
  { token: "{{heure}}", desc: "Heure de réservation" },
  { token: "{{evenement}}", desc: "Nom du tableau" },
  { token: "{{entreprise}}", desc: "Votre établissement" },
  { token: "{{logo}}", desc: "Votre logo" },
];

export function SendAlertModal({
  boardId,
  onClose,
  onSent,
}: {
  boardId: number;
  onClose: () => void;
  onSent: () => void;
}) {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [status, setStatus] = useState<{ pending: number; already_sent: number; no_email: number; with_email: number; last_alert_subject: string; last_alert_body: string } | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ sent: number; failed: number; reset: boolean } | null>(null);
  const [error, setError] = useState("");
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    moduleApi.getExceptionServiceBoardAlertStatus(boardId)
      .then((d: any) => {
        setStatus(d);
        // Si un message a déjà été envoyé sur ce tableau, on part de ce texte plutôt que du modèle par
        // défaut — sinon "changer le message" se déclencherait accidentellement dès la première frappe.
        if (d?.last_alert_subject) setSubject(d.last_alert_subject);
        if (d?.last_alert_body) setBody(d.last_alert_body);
      })
      .catch(() => setStatus(null))
      .finally(() => setLoadingStatus(false));
  }, [boardId]);

  const isNewCampaign = !!status?.last_alert_subject && (subject.trim() !== status.last_alert_subject || body.trim() !== status.last_alert_body);
  const recipientCount = isNewCampaign ? (status?.with_email ?? 0) : (status?.pending ?? 0);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setError("");
    try {
      const res: any = await moduleApi.sendExceptionServiceBoardAlert(boardId, { subject: subject.trim(), body: body.trim() });
      if (res?.error) { setError(res.error); return; }
      setResult({ sent: res.sent || 0, failed: res.failed || 0, reset: !!res.reset });
      onSent();
    } catch (e: any) {
      setError(e?.message || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
            <h2 className="text-base font-bold text-content flex items-center gap-2"><Mail size={17} className="text-brand-500" /> Envoyer une alerte au client</h2>
            <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft transition-all"><X size={18} /></button>
          </div>

          {result ? (
            <div className="p-6 space-y-3 text-center">
              <p className="text-sm text-content">
                <span className="font-black text-emerald-500">{result.sent}</span> email{result.sent > 1 ? "s" : ""} envoyé{result.sent > 1 ? "s" : ""}
                {result.failed > 0 && <> — <span className="font-black text-red-500">{result.failed}</span> échec{result.failed > 1 ? "s" : ""}</>}.
              </p>
              {result.reset && <p className="text-xs text-content-muted">Nouveau message : les compteurs de notification ont été réinitialisés pour tout le monde.</p>}
              <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">Fermer</button>
            </div>
          ) : (
            <>
              <div className="p-5 space-y-4 overflow-y-auto">
                {loadingStatus ? (
                  <div className="flex justify-center py-2"><Loader2 size={18} className="animate-spin text-content-muted" /></div>
                ) : status && (
                  <>
                    {isNewCampaign ? (
                      <div className="p-2.5 rounded-xl bg-warning-500/10 border border-warning-500/20 text-warning-500 text-xs font-semibold">
                        Message différent du dernier envoyé — sera renvoyé à <b>tout le monde</b> ({status.with_email} destinataire{status.with_email > 1 ? "s" : ""} avec email), y compris ceux déjà notifiés pour l'ancien message.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2.5 py-1 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 font-bold">{status.pending} destinataire{status.pending > 1 ? "s" : ""} prêt{status.pending > 1 ? "s" : ""}</span>
                        {status.already_sent > 0 && <span className="px-2.5 py-1 rounded-lg bg-surface-soft text-content-muted font-bold">{status.already_sent} déjà notifié{status.already_sent > 1 ? "s" : ""}</span>}
                      </div>
                    )}
                    {status.no_email > 0 && <div className="flex flex-wrap gap-2 text-xs"><span className="px-2.5 py-1 rounded-lg bg-surface-soft text-content-muted font-bold">{status.no_email} sans email (ne recevront rien)</span></div>}
                  </>
                )}

                <div>
                  <label className={labelCls}>Sujet</label>
                  <input type="text" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Message</label>
                  <textarea ref={bodyRef} className={`${inputCls} resize-none`} style={{ minHeight: "160px" }} value={body} onChange={(e) => setBody(e.target.value)} />
                </div>
                <TokenPicker tokens={TOKENS} textareaRef={bodyRef} value={body} onChange={setBody} />
                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
              </div>
              <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Annuler</button>
                <button
                  onClick={handleSend}
                  disabled={sending || !subject.trim() || !body.trim() || (status ? recipientCount === 0 : false)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50"
                >
                  <Send size={15} /> {sending ? "Envoi..." : status ? `Envoyer à ${recipientCount}` : "Envoyer"}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
